// Deposits and withdrawals for an account (accounts.js on the server): personal accounts
// can add and take out money (and adjust the balance to match the broker), funded accounts
// record payouts. They move the balance but never count as trading P&L (see accountStatus in balance.js).
import { useEffect, useState } from 'react';
import { api } from './api';
import { dayKey, netPnl, usd } from './pnl';
import { cashFlowAmount } from './balance';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

// What each account type allows, matching the server.
export function cashKinds(account) {
  if (!account) return [];
  if (account.type === 'Evaluation') return [];
  if (account.type === 'Funded' || account.type === 'Live') return ['withdrawal'];
  return ['deposit', 'withdrawal'];
}

const isFunded = (account) => account.type === 'Funded' || account.type === 'Live';
export const canAdjustBalance = (account) => !!account && !['Funded', 'Live', 'Evaluation'].includes(account.type);

function CashDialog({ account, kind, onClose, onChange, onSessionExpired }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ kind, amount: '', date: dayKey(new Date()), note: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const kinds = cashKinds(account);
  const funded = isFunded(account);
  const history = [...(account.cashFlows || [])].sort((a, b) => b.date.localeCompare(a.date));
  const label = (k) => (k === 'deposit' ? t('Deposit') : k === 'adjustment' ? t('Balance adjustment') : funded ? t('Payout') : t('Withdrawal'));

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function fail(err) {
    if (err.status === 401) return onSessionExpired();
    setError(err.message);
    return null;
  }

  async function save(e) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      onChange(await api(`/accounts/${account._id}/cash`, { method: 'POST', body: { ...form, amount: Number(form.amount) } }));
      onClose();
    } catch (err) {
      fail(err);
      setSaving(false);
    }
  }

  async function remove(id) {
    setError('');
    try {
      onChange(await api(`/accounts/${account._id}/cash/${id}`, { method: 'DELETE' }));
      setConfirmDelete(null);
    } catch (err) {
      fail(err);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal cash-modal" role="dialog" aria-modal="true" aria-labelledby="cash-title" onSubmit={save}>
        <div className="modal-header">
          <div>
            <h2 id="cash-title">{funded ? t('Record a payout') : t('Deposit or withdraw')}</h2>
            <p className="manual-trade-day">{account.name}</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>

        {kinds.length > 1 && (
          <div className="cash-kind" role="group" aria-label={t('Type')}>
            {kinds.map((k) => (
              <button key={k} type="button" className={`secondary small${form.kind === k ? ' active' : ''}`} aria-pressed={form.kind === k}
                onClick={() => setForm((f) => ({ ...f, kind: k }))}>{label(k)}</button>
            ))}
          </div>
        )}

        <div className="fields cash-fields">
          <label>
            {t('Amount ($)')}
            <input id="cash-amount" type="number" min="0.01" step="0.01" inputMode="decimal" required autoFocus
              value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="500" />
          </label>
          <label>
            {t('Date')}
            <input id="cash-date" type="date" required value={form.date} max={dayKey(new Date())}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
          </label>
          <label className="cash-note">
            {t('Note (optional)')}
            <input id="cash-note" maxLength={200} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder={funded ? t('e.g. First payout') : t('e.g. Monthly top-up')} />
          </label>
        </div>

        <p className="field-hint">
          {funded
            ? t('A payout lowers your balance. Your max loss level stays where it is, so you’ll have less room above it. Firms handle payouts differently, so check your firm’s rule. Payout winning days start counting again after each payout.')
            : t('This changes your balance only. It never counts as trading profit or loss in your stats.')}
        </p>
        {error && <p className="error">{error}</p>}

        <div className="modal-actions">
          <button type="submit" disabled={saving}>{saving ? t('Saving…') : t('Save {kind}', { kind: label(form.kind).toLowerCase() })}</button>
          <button type="button" className="secondary" onClick={onClose}>{t('Cancel')}</button>
        </div>

        {history.length > 0 && (
          <div className="cash-history">
            <span className="modal-section-label">{t('History')}</span>
            <ul>
              {history.map((f) => (
                <li key={f._id}>
                  <span className="cash-history-date">{new Date(`${f.date}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  <span className="cash-history-what">{label(f.kind)}{f.note ? ` · ${f.note}` : ''}</span>
                  <span className={`cash-history-amount ${cashFlowAmount(f) >= 0 ? 'gain' : 'loss'}`}>{cashFlowAmount(f) >= 0 ? '+' : '−'}{usd.format(Math.abs(f.amount))}</span>
                  {confirmDelete === f._id ? (
                    <span className="cash-history-confirm">
                      <button type="button" className="link-button danger-link" onClick={() => remove(f._id)}>{t('Yes, delete')}</button>
                      <button type="button" className="link-button" onClick={() => setConfirmDelete(null)}>{t('No')}</button>
                    </span>
                  ) : (
                    <button type="button" className="link-button" onClick={() => setConfirmDelete(f._id)}>{t('Delete')}</button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </form>
    </div>
  );
}

// Personal accounts: type in the balance the broker shows. With a starting balance, the
// difference is saved as a balance adjustment today; without one, the starting balance is
// set so the account lands on that number (trades and cash moves since then still count).
export function AdjustBalanceDialog({ account, balance, trades = [], onClose, onChange, onSessionExpired }) {
  const { t } = useI18n();
  const hasStart = !!account.size;
  // Without a starting balance there's no balance yet: what trades and cash moves add up to.
  const moved = trades.reduce((sum, x) => sum + netPnl(x), 0) + (account.cashFlows || []).reduce((sum, f) => sum + cashFlowAmount(f), 0);
  const current = hasStart ? balance : null;
  const [value, setValue] = useState(current != null ? String(Math.round(current * 100) / 100) : '');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const target = Math.round(Number(value) * 100) / 100;
  const valid = value !== '' && Number.isFinite(target) && target >= 0;
  const diff = valid && current != null ? Math.round((target - current) * 100) / 100 : null;
  const start = valid && !hasStart ? Math.round((target - moved) * 100) / 100 : null;

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function save(e) {
    e.preventDefault();
    setError('');
    if (hasStart && !diff) return setError(t('The balance is already that amount'));
    if (!hasStart && !(start > 0)) return setError(t('That’s less than what the trades in this account made, so the starting balance would be $0 or less.'));
    setSaving(true);
    try {
      onChange(await (hasStart
        ? api(`/accounts/${account._id}/cash`, { method: 'POST', body: { kind: 'adjustment', amount: diff, date: dayKey(new Date()), note: note.trim() } })
        : api(`/accounts/${account._id}`, { method: 'PATCH', body: { size: start } })));
      onClose();
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
      setSaving(false);
    }
    return null;
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal cash-modal" role="dialog" aria-modal="true" aria-labelledby="adjust-title" onSubmit={save}>
        <div className="modal-header">
          <div>
            <h2 id="adjust-title">{t('Adjust balance')}</h2>
            <p className="manual-trade-day">{account.name}</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>

        <div className="fields cash-fields">
          <label>
            {t('Balance now ($)')}
            <input id="adjust-balance" type="number" min="0" step="0.01" inputMode="decimal" required autoFocus
              value={value} onChange={(e) => setValue(e.target.value)} placeholder="10000" />
          </label>
          {hasStart && (
            <label className="cash-note">
              {t('Note (optional)')}
              <input id="adjust-note" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('e.g. Match my broker statement')} />
            </label>
          )}
        </div>

        <p className="field-hint">
          {hasStart
            ? diff
              ? t('Shown now: {current}. Saving adds a balance adjustment of {diff} today.', { current: usd.format(current), diff: `${diff > 0 ? '+' : '−'}${usd.format(Math.abs(diff))}` })
              : t('Shown now: {current}. Type the balance your broker shows.', { current: usd.format(current) })
            : t('This account has no starting balance yet. Saving sets it to {start}, so the balance shows {target} after its trades.', { start: start != null ? usd.format(start) : '—', target: valid ? usd.format(target) : '—' })}
          {' '}{t('It never counts as trading profit or loss, or as a deposit or withdrawal.')}
        </p>
        {error && <p className="error">{error}</p>}

        <div className="modal-actions">
          <button type="submit" disabled={saving || !valid}>{saving ? t('Saving…') : t('Save balance')}</button>
          <button type="button" className="secondary" onClick={onClose}>{t('Cancel')}</button>
        </div>
      </form>
    </div>
  );
}

// The buttons on the account's balance card, and the totals so far.
export function CashFlowActions({ account, status, onChange, onSessionExpired }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(null);
  const kinds = cashKinds(account);
  if (!kinds.length) return null;
  const funded = isFunded(account);
  const cash = status && status.cash;

  return (
    <div className="cash-actions">
      {cash && (
        <span className="cash-totals">
          {cash.deposits > 0 && <span>{t('Deposits')} <strong className="gain">+{usd.format(cash.deposits)}</strong></span>}
          {cash.withdrawals > 0 && <span>{funded ? t('Payouts') : t('Withdrawals')} <strong className="loss">−{usd.format(cash.withdrawals)}</strong></span>}
          {cash.adjusted ? <span>{t('Adjustments')} <strong className={cash.adjusted > 0 ? 'gain' : 'loss'}>{cash.adjusted > 0 ? '+' : '−'}{usd.format(Math.abs(cash.adjusted))}</strong></span> : null}
        </span>
      )}
      <span className="cash-buttons">
        {funded ? (
          <button type="button" className="secondary small" onClick={() => setOpen('withdrawal')}>{t('Record a payout')}</button>
        ) : (
          <>
            <button type="button" className="secondary small" onClick={() => setOpen('deposit')}>+ {t('Deposit')}</button>
            <button type="button" className="secondary small" onClick={() => setOpen('withdrawal')}>− {t('Withdraw')}</button>
            {canAdjustBalance(account) && <button type="button" className="secondary small" onClick={() => setOpen('adjust')}>{t('Adjust balance')}</button>}
          </>
        )}
      </span>
      {open === 'adjust' ? <AdjustBalanceDialog account={account} balance={status ? status.balance : null} onClose={() => setOpen(null)} onChange={onChange} onSessionExpired={onSessionExpired} />
        : open && <CashDialog account={account} kind={open} onClose={() => setOpen(null)} onChange={onChange} onSessionExpired={onSessionExpired} />}
    </div>
  );
}
